import React from 'react';
import { Field, Form } from 'react-final-form';
import PropTypes from 'prop-types';
import Axios from 'axios';
import { required } from 'common/Forms/Validators';
import showToast from 'common/Toast';
import PasswordRequirements from '@/components/shared/PasswordRequirements';
import { strictPasswordValidationError } from '@/components/shared/PasswordRequirements/helpers';
import Footer from '../Footer';
import styles from './MyAccount.module.scss';

function passwordsMatch(passwords) {
  return passwords.password === passwords.password_confirmation;
}

export function submit(studentPath, values) {
  return Axios.put(studentPath, values).then((response) => {
    if (response.data.success) {
      showToast('Password change successful');
    }
    else {
      showToast('Password change failed', { msgType: 'error' });
    }
  }).catch(() => {
    showToast('Password change failed', { msgType: 'error' });
  });
}

export default function ChangePasswordForm({
  hasStrictPasswordRequirement = false, studentPath, onSubmit
}) {
  return (
    <Form
      validate={(values) => {
        const errors = { student: {} };
        if (values.student && !passwordsMatch(values.student)) {
          errors.student.password_confirmation = "Passwords don't match";
        }

        const passwordVal = values.student?.password;
        if (hasStrictPasswordRequirement) {
          const message = strictPasswordValidationError(passwordVal);
          if (message) {
            errors.student.password = message;
          }
        }
        else if (passwordVal && passwordVal.length < 6) {
          errors.student.password = 'Password must be at least 6 characters long';
        }

        return errors;
      }}
      onSubmit={values => onSubmit(studentPath, values)}
    >
      {({ handleSubmit, hasValidationErrors, values }) => (
        <form onSubmit={handleSubmit}>
          <div className={styles.fieldGroup}>
            <label className={`bold ${styles.label}`} htmlFor="new-password">
              New Password
            </label>
            <Field
              className={styles.input}
              component="input"
              id="new-password"
              name="student[password]"
              type="password"
              validate={required}
            />
            {hasStrictPasswordRequirement && (
              <PasswordRequirements
                maxWidth={290}
                password={values.student?.password || ''}
              />
            )}
          </div>

          <div className={styles.fieldGroup}>
            <Field
              name="student[password_confirmation]"
              type="password"
            >
              {({ input, meta }) => (
                <div>
                  <div>
                    <label className={`bold ${styles.label}`} htmlFor="confirm-password">
                      Confirm Password
                    </label>

                    <input
                      className={styles.input}
                      id="confirm-password"
                      type="password"
                      {...input}
                    />
                  </div>

                  {
                    meta.error && meta.touched &&
                    <div className={styles.fieldError}>{meta.error}</div>
                  }
                </div>
              )}
            </Field>
          </div>

          <div className={styles.divider} />

          <div>
            <p className="bold mb20">
              Your current password is required for all changes to your account settings
            </p>

            <div className={styles.fieldGroup}>
              <label className={`bold ${styles.label}`} htmlFor="current-password">
                Confirm Password
              </label>
              <Field
                className={styles.input}
                component="input"
                id="current-password"
                name="student[current_password]"
                type="password"
                validate={required}
              />
            </div>
          </div>
          <Footer disabled={hasValidationErrors} />
        </form>
      )}
    </Form>
  );
}

ChangePasswordForm.propTypes = {
  hasStrictPasswordRequirement: PropTypes.bool.isRequired,
  studentPath: PropTypes.string.isRequired,
  onSubmit: PropTypes.func
};

ChangePasswordForm.defaultProps = {
  onSubmit: submit
};
